import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useChat } from '../../../hooks/useChat';
import Icon from '../../../components/AppIcon';
import toast from 'react-hot-toast';

const AIRecommendations = ({ professionals }) => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [userInput, setUserInput] = useState('');
  const [recommendations, setRecommendations] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);
  const inputRef = useRef(null);

  const { response, isLoading, error, sendMessage } = useChat('OPEN_AI', 'gpt-5', false);

  useEffect(() => {
    if (error) toast?.error('AI recommendation failed. Please try again.');
  }, [error]);

  useEffect(() => {
    if (response && hasSearched) {
      try {
        const jsonMatch = response?.match(/\{[\s\S]*\}/);
        if (jsonMatch) {
          const parsed = JSON.parse(jsonMatch?.[0]);
          setRecommendations(parsed);
        }
      } catch {
        setRecommendations({ summary: response, matches: [] });
      }
    }
  }, [response, hasSearched]);

  const handleGetRecommendations = () => {
    if (!userInput?.trim() || isLoading) return;

    setHasSearched(true);
    setRecommendations(null);

    const profSummaries = professionals?.slice(0, 9)?.map(p => ({
      id: p?.id,
      name: p?.name,
      category: p?.categoryLabel,
      location: p?.location,
      rating: p?.rating,
      reviewCount: p?.reviewCount,
      startingPrice: p?.startingPrice,
      specializations: p?.specializations?.join(', '),
      completedJobs: p?.completedJobs,
      verified: p?.verified,
      responseTime: p?.responseTime,
    }));

    const systemPrompt = `You are an expert property professional matchmaker. Analyze the user's needs and recommend the best professionals from the provided list.

Return ONLY valid JSON in this exact format:
{
  "summary": "Brief explanation of your recommendations (2-3 sentences)",
  "matches": [
    {
      "id": "professional id",
      "name": "professional name",
      "reason": "Why this professional is a great match (1-2 sentences)",
      "matchScore": 95
    }
  ]
}

Return top 3 matches maximum. matchScore is 0-100.`;

    sendMessage([
      { role: 'system', content: systemPrompt },
      { role: 'user', content: `User needs: ${userInput}\n\nAvailable professionals:\n${JSON.stringify(profSummaries, null, 2)}` }
    ], { max_completion_tokens: 800 });
  };

  const getMatchedProfessional = (id) => professionals?.find(p => p?.id === id);

  const categoryColors = {
    Lawyer: { color: '#A78BFA', bg: 'rgba(123,47,255,0.15)', border: 'rgba(123,47,255,0.3)' },
    Photographer: { color: '#00D4FF', bg: 'rgba(0,212,255,0.15)', border: 'rgba(0,212,255,0.3)' },
    Stylist: { color: '#FF6B9D', bg: 'rgba(255,107,157,0.15)', border: 'rgba(255,107,157,0.3)' },
  };

  const quickPrompts = [
    'I need a lawyer for first home purchase in Sydney',
    'Looking for a photographer for a luxury apartment in Melbourne',
    'Need a stylist to stage my vacant property in Brisbane',
    'Best verified professional with fast response time',
  ];

  return (
    <div className="mb-6">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-5 py-4 rounded-xl font-semibold text-sm transition-all duration-200"
        style={{
          background: isOpen
            ? 'linear-gradient(135deg, rgba(0,212,255,0.15), rgba(123,47,255,0.15))'
            : 'rgba(255,255,255,0.04)',
          border: isOpen ? '1px solid rgba(0,212,255,0.4)' : '1px solid rgba(0,212,255,0.15)',
          color: '#fff',
        }}
      >
        <div className="flex items-center gap-3">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center"
            style={{ background: 'linear-gradient(135deg, rgba(0,212,255,0.2), rgba(123,47,255,0.2))' }}
          >
            <Icon name="Sparkles" size={16} color="#00D4FF" />
          </div>
          <div className="text-left">
            <p className="text-sm font-bold text-white">AI Professional Matcher</p>
            <p className="text-xs" style={{ color: '#6B9EC4' }}>Describe your needs — AI finds your best match</p>
          </div>
        </div>
        <Icon name={isOpen ? 'ChevronUp' : 'ChevronDown'} size={18} color="#6B9EC4" />
      </button>
      {isOpen && (
        <div
          className="mt-2 rounded-xl p-5 space-y-4"
          style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(0,212,255,0.15)' }}
        >
          <div className="flex gap-2">
            <input
              ref={inputRef}
              type="text"
              value={userInput}
              onChange={(e) => setUserInput(e?.target?.value)}
              onKeyDown={(e) => e?.key === 'Enter' && handleGetRecommendations()}
              placeholder="e.g. I need a conveyancing lawyer in Sydney for a first home purchase..."
              className="flex-1 px-4 py-3 rounded-xl text-sm outline-none transition-all"
              style={{
                background: 'rgba(255,255,255,0.06)',
                border: '1px solid rgba(0,212,255,0.2)',
                color: '#E2E8F0',
              }}
              disabled={isLoading}
            />
            <button
              onClick={handleGetRecommendations}
              disabled={!userInput?.trim() || isLoading}
              className="px-4 py-3 rounded-xl font-semibold text-sm transition-all duration-200 flex items-center gap-2 flex-shrink-0"
              style={{
                background:
                  !userInput?.trim() || isLoading
                    ? 'rgba(0,212,255,0.1)'
                    : 'linear-gradient(135deg, #00D4FF, #0099CC)',
                color: !userInput?.trim() || isLoading ? '#6B9EC4' : '#000',
                cursor: !userInput?.trim() || isLoading ? 'not-allowed' : 'pointer',
              }}
            >
              {isLoading ? (
                <>
                  <div
                    className="w-4 h-4 rounded-full border-2 border-t-transparent animate-spin"
                    style={{ borderColor: '#6B9EC4', borderTopColor: 'transparent' }}
                  />
                  Analysing
                </>
              ) : (
                <>
                  <Icon name="Sparkles" size={15} />
                  Match Me
                </>
              )}
            </button>
          </div>

          {!hasSearched && (
            <div>
              <p className="text-xs mb-2" style={{ color: '#6B9EC4' }}>Quick prompts:</p>
              <div className="flex flex-wrap gap-2">
                {quickPrompts?.map((prompt, i) => (
                  <button
                    key={i}
                    onClick={() => {
                      setUserInput(prompt);
                      inputRef?.current?.focus();
                    }}
                    className="px-3 py-1.5 rounded-lg text-xs transition-all hover:opacity-80"
                    style={{
                      background: 'rgba(0,212,255,0.08)',
                      border: '1px solid rgba(0,212,255,0.15)',
                      color: '#A0B4C8',
                    }}
                  >
                    {prompt}
                  </button>
                ))}
              </div>
            </div>
          )}

          {isLoading && (
            <div className="flex items-center gap-3 py-4">
              <div
                className="w-5 h-5 rounded-full border-2 animate-spin flex-shrink-0"
                style={{ borderColor: 'rgba(0,212,255,0.3)', borderTopColor: '#00D4FF' }}
              />
              <p className="text-sm" style={{ color: '#6B9EC4' }}>
                AI is analysing your requirements and matching professionals...
              </p>
            </div>
          )}

          {recommendations && !isLoading && (
            <div className="space-y-4">
              {recommendations?.summary && (
                <div
                  className="flex gap-3 p-4 rounded-xl"
                  style={{ background: 'rgba(0,212,255,0.06)', border: '1px solid rgba(0,212,255,0.15)' }}
                >
                  <Icon name="Sparkles" size={16} color="#00D4FF" className="flex-shrink-0 mt-0.5" />
                  <p className="text-sm leading-relaxed" style={{ color: '#A0B4C8' }}>
                    {recommendations?.summary}
                  </p>
                </div>
              )}

              {recommendations?.matches?.length > 0 && (
                <div className="space-y-3">
                  <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#6B9EC4' }}>
                    Top Matches
                  </p>
                  {recommendations?.matches?.map((match, idx) => {
                    const pro = getMatchedProfessional(match?.id);
                    if (!pro) return null;
                    const colors = categoryColors?.[pro?.categoryLabel] || categoryColors?.Lawyer;
                    return (
                      <div
                        key={match?.id}
                        className="flex items-start gap-4 p-4 rounded-xl transition-all duration-200 hover:scale-[1.01] cursor-pointer"
                        style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(0,212,255,0.1)' }}
                        onClick={() => navigate(`/professional-profile?id=${pro?.id}`)}
                      >
                        <div className="flex-shrink-0 relative">
                          <div
                            className="w-12 h-12 rounded-xl flex items-center justify-center text-lg font-bold"
                            style={{ background: colors?.bg, border: `1px solid ${colors?.border}`, color: colors?.color }}
                          >
                            {pro?.name?.charAt(0)}
                          </div>
                          <div
                            className="absolute -top-1.5 -left-1.5 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold"
                            style={{ background: idx === 0 ? '#FFD700' : idx === 1 ? '#C0C0C0' : '#CD7F32', color: '#000' }}
                          >
                            {idx + 1}
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5">
                            <p className="text-sm font-bold text-white truncate">{pro?.name}</p>
                            {pro?.verified && <Icon name="BadgeCheck" size={13} color="#00D4FF" />}
                          </div>
                          <p className="text-xs mb-1" style={{ color: colors?.color }}>
                            {pro?.categoryLabel} · {pro?.location}
                          </p>
                          <p className="text-xs leading-relaxed mb-2" style={{ color: '#A0B4C8' }}>
                            {match?.reason}
                          </p>
                          <div className="flex items-center gap-3">
                            <span className="text-xs" style={{ color: '#FFD700' }}>★ {pro?.rating}</span>
                            <span className="text-xs" style={{ color: '#6B9EC4' }}>{pro?.startingPrice}</span>
                            <div
                              className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs"
                              style={{ background: 'rgba(0,255,136,0.1)', color: '#00FF88' }}
                            >
                              <Icon name="Zap" size={10} />
                              {match?.matchScore}% match
                            </div>
                          </div>
                        </div>
                        <Icon name="ChevronRight" size={16} color="rgba(0,212,255,0.5)" className="flex-shrink-0 mt-1" />
                      </div>
                    );
                  })}
                </div>
              )}

              <button
                onClick={() => {
                  setUserInput('');
                  setRecommendations(null);
                  setHasSearched(false);
                  inputRef?.current?.focus();
                }}
                className="text-xs flex items-center gap-1 transition-all hover:opacity-80"
                style={{ color: '#6B9EC4' }}
              >
                <Icon name="RotateCcw" size={12} /> Start new search
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AIRecommendations;
